import useBi from '@haaretz/s-use-bi';
import * as React from 'react';
// A memoized map of all initialized observers, one per config.
const observers = new Map();
// We use this to store elements that were already reported as
// having entered the viewport in order to be able to avoid
// registering them in the observer again if the component rerenders
const intersectedElements = new WeakSet();
// We don't need this on the server
const impressionEvent = typeof CustomEvent !== 'undefined' && new CustomEvent('biImpression', // The custom even should not bubble up so that it doesn't
// accidentally trigger observers on parent elements, if any exist.
{
    bubbles: false
});
// TODO:
// This is just an example. Not sure what the default values should be
const defaultConfig = {
    threshold: 1
};
export default function useImpressionObserver({ elementRef, biData, config = defaultConfig, disabled = false, onObserve }) {
    const sendBiData = useBi('impression');
    React.useEffect(()=>{
        const element = elementRef && elementRef.current;
        if (disabled || !element) return undefined;
        const sendImpression = onObserve ? (data)=>{
            onObserve(data);
            sendBiData(data);
        } : sendBiData;
        const unobserve = observeImpression({
            element,
            biData,
            sendImpression,
            config
        });
        return unobserve;
    }, [
        biData,
        config,
        disabled,
        elementRef,
        onObserve,
        sendBiData
    ]);
}
function observerCallback(entries, _observer) {
    for (const entry of entries){
        const { isIntersecting, target } = entry;
        if (isIntersecting && impressionEvent) target.dispatchEvent(impressionEvent);
    }
}
function observeImpression({ element, biData, sendImpression, config = defaultConfig }) {
    let wasCleanedUp = false;
    const serializedConfig = JSON.stringify(config);
    if (intersectedElements.has(element)) return ()=>undefined;
    const existingObserver = observers.get(serializedConfig);
    const observerInfo = existingObserver || {
        observer: new IntersectionObserver(observerCallback, config),
        observed: 0
    };
    if (!existingObserver) observers.set(serializedConfig, observerInfo);
    const { observer } = observerInfo;
    observer.observe(element);
    observerInfo.observed += 1;
    element.addEventListener('biImpression', onObserve);
    return unobserve;
    function unobserve() {
        observer.unobserve(element);
        element.removeEventListener('biImpression', onObserve);
        // Only decrease the number of observed elements once
        if (!wasCleanedUp) observerInfo.observed -= 1;
        wasCleanedUp = true;
        if (observerInfo.observed === 0) {
            observer.disconnect();
            observers.delete(serializedConfig);
        }
    }
    function onObserve(evt) {
        evt.stopPropagation();
        sendImpression(biData);
        intersectedElements.add(element);
        unobserve();
    }
}

//# sourceMappingURL=useImpressionObserver.js.map